import { useState } from "react";
import { Card } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

const TOGGLE_ITEM_CLS =
  "h-auto text-[12px] px-3 py-1 border-black/15 bg-white text-gray-600 font-normal hover:bg-gray-50 hover:text-gray-600 transition-colors data-[state=on]:bg-[var(--brand-dark-teal)] data-[state=on]:text-white data-[state=on]:border-[var(--brand-dark-teal)] data-[state=on]:z-10 disabled:opacity-40 disabled:cursor-not-allowed";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { DrillRow, PinnedRow } from "@/hooks/useDrillData";
import type { FilterState, ScopeFilters } from "@/types/filters";
import { selectedDrillId } from "@/lib/drillScope";
import type { ComputedMetrics } from "@/types/metrics";
import { METRIC_TOOLTIPS, type MetricTooltipKey } from "@/lib/metricTooltips";

function fmtPct(n: number) {
  return `${(n * 100).toFixed(1)}%`;
}
function fmt1(n: number) {
  return n.toFixed(1);
}
function flagged(display: string, isFlagged: boolean): React.ReactNode {
  if (!isFlagged) return display;
  return <span style={{ color: "var(--perf-red)", fontWeight: 600 }}>{display}</span>;
}

type UtilUnit = "pct" | "hours";
type SortDir = "asc" | "desc";
type SortKey =
  | "label"
  | "petSeenRate"
  | "otherTypesRate"
  | "availableTimeRate"
  | "blockoffRate"
  | "avgSeenHoursPerDvmPerDay"
  | "avgOtherTypesHoursPerDvmPerDay"
  | "avgAvailHoursPerDvmPerDay"
  | "avgBlockoffHoursPerDvmPerDay"
  | "avgWorkedHoursPerDay"
  | "avgHoursPerDay"
  | "avgHoursPerDvmPerWeek"
  | "daysPerDvmPerWeek"
  | "dvmsPerDay"
  | "avgHoursPerWeekTotal"
  | "avgDvmDaysPerWeek";

interface ColDef {
  id: string;
  key: SortKey;
  label: string;
  group: "util" | "avgCap" | "totalCap";
  // Hover definition. Optional because four columns are deliberately bare --
  // Completed Appts, Avg Appt Time, vs. PY and Total days/wk.
  tip?: MetricTooltipKey;
}

// Seen/Other/Avail/Block family equivalents across units
const SEEN: Record<UtilUnit, SortKey> = {
  pct: "petSeenRate",
  hours: "avgSeenHoursPerDvmPerDay",
};
const OTHER: Record<UtilUnit, SortKey> = {
  pct: "otherTypesRate",
  hours: "avgOtherTypesHoursPerDvmPerDay",
};
const AVAIL: Record<UtilUnit, SortKey> = {
  pct: "availableTimeRate",
  hours: "avgAvailHoursPerDvmPerDay",
};
const BLOCK: Record<UtilUnit, SortKey> = {
  pct: "blockoffRate",
  hours: "avgBlockoffHoursPerDvmPerDay",
};
// total util family (pct has no total column)
const TOTAL_UTIL: Partial<Record<UtilUnit, SortKey>> = {
  hours: "avgWorkedHoursPerDay",
};

type UtilFamily = "seen" | "other" | "avail" | "block" | "totalUtil";
const KEY_FAMILY: Partial<Record<SortKey, UtilFamily>> = {
  petSeenRate: "seen",
  avgSeenHoursPerDvmPerDay: "seen",
  otherTypesRate: "other",
  avgOtherTypesHoursPerDvmPerDay: "other",
  availableTimeRate: "avail",
  avgAvailHoursPerDvmPerDay: "avail",
  blockoffRate: "block",
  avgBlockoffHoursPerDvmPerDay: "block",
  avgWorkedHoursPerDay: "totalUtil",
  // avgHoursPerDay is a CAP column only -- the Utilization total is worked hours.
};

function mapSortToUnit(key: SortKey, toUnit: UtilUnit): SortKey | null {
  const family = KEY_FAMILY[key];
  if (!family) return null; // cap column — no cross-unit mapping
  if (family === "seen") return SEEN[toUnit];
  if (family === "other") return OTHER[toUnit];
  if (family === "avail") return AVAIL[toUnit];
  if (family === "block") return BLOCK[toUnit];
  if (family === "totalUtil") return TOTAL_UTIL[toUnit] ?? null; // null in pct mode
  return null;
}

function defaultSort(unit: UtilUnit): SortKey {
  return SEEN[unit];
}

function getUtilCols(unit: UtilUnit, typeFilterActive: boolean): ColDef[] {
  const g = "util" as const;
  switch (unit) {
    case "pct":
      return [
        { id: "seen-pct", key: "petSeenRate", label: "Seen %", group: g, tip: "seenPct" },
        ...(typeFilterActive
          ? [
              {
                id: "other-pct",
                key: "otherTypesRate" as const,
                label: "Other %",
                tip: "otherTypes" as const,
                group: g,
              },
            ]
          : []),
        { id: "block-pct", key: "blockoffRate", label: "Block %", group: g, tip: "blockoffPct" },
        {
          id: "avail-pct",
          key: "availableTimeRate",
          label: "Avail %",
          group: g,
          tip: "availablePct",
        },
      ];
    case "hours":
      return [
        {
          id: "seen-hrs",
          key: "avgSeenHoursPerDvmPerDay",
          label: "Seen hrs",
          group: g,
          tip: "seen",
        },
        ...(typeFilterActive
          ? [
              {
                id: "other-hrs",
                key: "avgOtherTypesHoursPerDvmPerDay" as const,
                label: "Other hrs",
                tip: "otherTypes" as const,
                group: g,
              },
            ]
          : []),
        {
          id: "block-hrs",
          key: "avgBlockoffHoursPerDvmPerDay",
          label: "Block hrs",
          group: g,
          tip: "blockoff",
        },
        {
          id: "avail-hrs",
          key: "avgAvailHoursPerDvmPerDay",
          label: "Avail hrs",
          group: g,
          tip: "available",
        },
        {
          id: "total-hrs-util",
          key: "avgWorkedHoursPerDay",
          label: "Worked hrs/day",
          group: g,
          tip: "worked",
        },
      ];
  }
}

function getAvgCapCols(grain: FilterState["drillLevel"]): ColDef[] {
  const g = "avgCap" as const;
  const cols: ColDef[] = [
    {
      id: "cap-hrsweek",
      key: "avgHoursPerDvmPerWeek",
      label: "Hrs/DVM/wk",
      group: g,
      tip: "hoursPerDvmPerWeek",
    },
    {
      id: "cap-hrsday",
      key: "avgHoursPerDay",
      label: "Hrs/DVM/day",
      group: g,
      tip: "hoursPerDvmPerDay",
    },
    {
      id: "cap-daysweek",
      key: "daysPerDvmPerWeek",
      label: "Days/wk",
      group: g,
      tip: "daysPerDvmPerWeek",
    },
  ];
  if (grain !== "dvm") {
    cols.push({
      id: "cap-dvmsday",
      key: "dvmsPerDay",
      label: "DVMs/day",
      group: g,
      tip: "dvmsPerDayPerSite",
    });
  }
  return cols;
}

function getTotalCapCols(): ColDef[] {
  const g = "totalCap" as const;
  return [
    {
      id: "tcap-hrsweek",
      key: "avgHoursPerWeekTotal",
      label: "Total hrs/wk",
      group: g,
      tip: "totalHoursPerWeek",
    },
    { id: "tcap-daysweek", key: "avgDvmDaysPerWeek", label: "Total days/wk", group: g },
  ];
}

// Size-scaled metrics shown per site so aggregate rows (country/group/division,
// pinned averages) are comparable with individual site rows. Per-DVM metrics are
// already normalized and left untouched.
const PER_SITE_KEYS = new Set<SortKey>(["dvmsPerDay", "avgHoursPerWeekTotal", "avgDvmDaysPerWeek"]);

function perSite(value: number, key: SortKey, siteCount: number): number {
  return PER_SITE_KEYS.has(key) && siteCount > 1 ? value / siteCount : value;
}

function getSortValue(row: DrillRow, key: SortKey): string | number {
  if (key === "label") return row.label;
  const v = (row.metrics[key as keyof ComputedMetrics] as number) ?? 0;
  return perSite(v, key, row.siteCount);
}

function cellValue(
  m: ComputedMetrics,
  key: SortKey,
  seenThreshold: number,
  availCeiling: number,
  siteCount: number
): React.ReactNode {
  const seenLow = m.petSeenRate <= seenThreshold;
  const availHigh = m.availableTimeRate >= availCeiling;
  switch (key) {
    case "label":
      return null;
    // util pct
    case "petSeenRate":
      return flagged(fmtPct(m.petSeenRate), seenLow);
    case "otherTypesRate":
      return fmtPct(m.otherTypesRate);
    case "availableTimeRate":
      return flagged(fmtPct(m.availableTimeRate), availHigh);
    case "blockoffRate":
      return fmtPct(m.blockoffRate);
    // util hours
    case "avgSeenHoursPerDvmPerDay":
      return flagged(fmt1(m.avgSeenHoursPerDvmPerDay), seenLow);
    case "avgOtherTypesHoursPerDvmPerDay":
      return fmt1(m.avgOtherTypesHoursPerDvmPerDay);
    case "avgAvailHoursPerDvmPerDay":
      return flagged(fmt1(m.avgAvailHoursPerDvmPerDay), availHigh);
    case "avgBlockoffHoursPerDvmPerDay":
      return fmt1(m.avgBlockoffHoursPerDvmPerDay);
    // The Utilization group's total: hours actually worked, which its four
    // segments sum to. Distinct from the Capacity group's Hrs/DVM/day below,
    // which is the roster figure.
    case "avgWorkedHoursPerDay":
      return fmt1(m.avgWorkedHoursPerDay);
    // avg cap
    case "avgHoursPerDay":
      return fmt1(m.avgHoursPerDay);
    case "avgHoursPerDvmPerWeek":
      return fmt1(m.avgHoursPerDvmPerWeek);
    case "daysPerDvmPerWeek":
      return fmt1(m.daysPerDvmPerWeek);
    case "dvmsPerDay":
      return fmt1(perSite(m.dvmsPerDay, key, siteCount));
    // total cap
    case "avgHoursPerWeekTotal":
      return fmt1(perSite(m.avgHoursPerWeekTotal, key, siteCount));
    case "avgDvmDaysPerWeek":
      return fmt1(perSite(m.avgDvmDaysPerWeek, key, siteCount));
  }
}

function buildCsv(
  rows: DrillRow[],
  capCols: ColDef[],
  entityLabel: string,
  typeFilterActive: boolean
): string {
  const pctCols = getUtilCols("pct", typeFilterActive);
  const hrsCols = getUtilCols("hours", typeFilterActive);
  const allCols = [...pctCols, ...hrsCols, ...capCols];
  const header = [entityLabel, "site_selected", ...allCols.map((c) => c.label)].join(",");
  const dataRows = rows.map((r) => {
    const cells = allCols.map((c) => {
      const v = getSortValue(r, c.key);
      return typeof v === "string" ? `"${v}"` : (v as number).toFixed(2);
    });
    return [`"${r.label}"`, r.siteCount, ...cells].join(",");
  });
  return [header, ...dataRows].join("\n");
}

const DRILL_LEVELS: FilterState["drillLevel"][] = ["country", "group", "division", "site", "dvm"];

const TOTAL_CAP_GRAINS = new Set<FilterState["drillLevel"]>(["site", "dvm"]);

interface DrillDownTableProps {
  rows: DrillRow[];
  pinned: PinnedRow[];
  filters: FilterState;
  setDrillLevel: (level: FilterState["drillLevel"]) => void;
  onRowClick: (patch: Partial<ScopeFilters>) => void;
  globalSeenThreshold: number;
  globalAvailCeiling: number;
}

function SortArrow({
  colKey,
  activeKey,
  dir,
}: {
  colKey: SortKey;
  activeKey: SortKey;
  dir: SortDir;
}) {
  if (activeKey !== colKey) return <span className="opacity-20 ml-0.5 text-[9px]">↕</span>;
  return (
    <span className="ml-0.5 text-[9px]" style={{ color: "var(--brand-dark-teal)" }}>
      {dir === "asc" ? "↑" : "↓"}
    </span>
  );
}

export function DrillDownTable({
  rows,
  pinned,
  filters,
  setDrillLevel,
  onRowClick,
  globalSeenThreshold,
  globalAvailCeiling,
}: DrillDownTableProps) {
  const grain = filters.drillLevel;
  const typeFilterActive = filters.appointmentTypes.length > 0;
  const totalCapAllowed = TOTAL_CAP_GRAINS.has(grain);

  const [unit, setUnit] = useState<UtilUnit>("pct");
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: "petSeenRate",
    dir: "desc",
  });

  function handleUnitChange(newUnit: UtilUnit) {
    const mapped = mapSortToUnit(sort.key, newUnit);
    if (mapped) {
      setSort((s) => ({ ...s, key: mapped }));
    }
    // If no mapping (cap column), keep the key — it will still exist in the new unit's columns
    setUnit(newUnit);
  }

  const utilCols = getUtilCols(unit, typeFilterActive);
  const avgCapCols = getAvgCapCols(grain);
  const totalCapCols = totalCapAllowed ? getTotalCapCols() : [];
  const capCols = [...avgCapCols, ...totalCapCols];

  // If current sort key no longer exists in active columns, fall back to default
  const allActiveKeys = new Set<SortKey>([
    "label",
    ...utilCols.map((c) => c.key),
    ...capCols.map((c) => c.key),
  ]);
  const activeSortKey = allActiveKeys.has(sort.key) ? sort.key : defaultSort(unit);

  function toggleSort(key: SortKey) {
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }
    );
  }

  const sorted = [...rows].sort((a, b) => {
    const av = getSortValue(a, activeSortKey);
    const bv = getSortValue(b, activeSortKey);
    const cmp =
      typeof av === "string" ? av.localeCompare(bv as string) : (av as number) - (bv as number);
    return sort.dir === "asc" ? cmp : -cmp;
  });

  const selectedId = selectedDrillId(filters.scope, grain);

  function handleRowClick(row: DrillRow) {
    switch (grain) {
      case "country":
        onRowClick({ countryCode: row.id });
        break;
      case "division":
        onRowClick({ divisionName: row.id });
        break;
      case "group":
        onRowClick({ groupName: row.id });
        break;
      case "site":
        onRowClick({ locationId: row.id });
        break;
      case "dvm":
        onRowClick({ resourceId: row.id });
        break;
    }
  }

  const entityLabel = grain === "dvm" ? "DVM" : grain.charAt(0).toUpperCase() + grain.slice(1);

  function renderCell(m: ComputedMetrics, col: ColDef, siteCount: number) {
    return cellValue(m, col.key, globalSeenThreshold, globalAvailCeiling, siteCount);
  }

  function renderPinnedCell(m: ComputedMetrics, col: ColDef, siteCount: number) {
    return cellValue(m, col.key, 0, 1, siteCount);
  }

  return (
    <TooltipProvider>
      <Card className="px-5 py-4 gap-0 shadow-none border-black/10">
        {/* Header row */}
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="text-[16px] font-medium">Drill-down</div>
            <button
              onClick={() => {
                const csv = buildCsv(sorted, capCols, entityLabel, typeFilterActive);
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `fetchtime_${grain}_${filters.customStart}_to_${filters.customEnd}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="text-[12px] px-3 py-1 border border-black/15 rounded bg-white text-gray-500 hover:bg-gray-50 transition-colors"
            >
              Export CSV
            </button>
          </div>

          <div className="flex items-center gap-2">
            {/* Unit toggle */}
            <ToggleGroup
              type="single"
              variant="outline"
              value={unit}
              onValueChange={(v) => {
                if (v) handleUnitChange(v as UtilUnit);
              }}
            >
              <ToggleGroupItem value="pct" className={TOGGLE_ITEM_CLS}>
                %
              </ToggleGroupItem>
              <ToggleGroupItem value="hours" className={TOGGLE_ITEM_CLS}>
                Hours
              </ToggleGroupItem>
            </ToggleGroup>

            {/* Grain toggle */}
            <ToggleGroup
              type="single"
              variant="outline"
              value={grain}
              onValueChange={(v) => {
                if (v) setDrillLevel(v as FilterState["drillLevel"]);
              }}
            >
              {DRILL_LEVELS.map((level) => (
                <ToggleGroupItem key={level} value={level} className={TOGGLE_ITEM_CLS}>
                  {level === "dvm" ? "DVM" : level.charAt(0).toUpperCase() + level.slice(1)}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        </div>

        <p className="text-[12px] text-gray-400 mb-3">
          Click a column header to sort · Click a row to filter the dashboard · Shift + scroll to
          scroll horizontally
        </p>

        <ScrollArea className="h-[560px] [&_[data-slot=table-container]]:overflow-visible">
          <Table className="text-[13px] table-fixed">
            {/* Fixed column widths — keeps layout stable when toggling % / Hours */}
            <colgroup>
              <col style={{ width: "auto", minWidth: "160px", maxWidth: "260px" }} />
              {[...utilCols, ...capCols].map((col) => (
                <col key={col.id} style={{ width: "98px" }} />
              ))}
            </colgroup>
            {/* Sticky lives on the th cells (with opaque backgrounds), not the
              thead — sticky row-groups have paint-order bugs in Chromium */}
            <TableHeader>
              {/* Group header row */}
              <TableRow className="border-b-0">
                <TableHead className="px-2 py-0.5 h-[22px] bg-white sticky top-0 z-[3]" />
                <TableHead
                  colSpan={utilCols.length}
                  className="px-2 py-0.5 h-[22px] text-center text-[11px] font-medium text-gray-400 bg-gray-50 border border-black/10 rounded-tl-sm rounded-tr-sm sticky top-0 z-[3]"
                >
                  Utilization
                </TableHead>
                {capCols.length > 0 && (
                  <TableHead
                    colSpan={capCols.length}
                    className="px-2 py-0.5 h-[22px] text-center text-[11px] font-medium text-gray-400 bg-gray-50 border border-black/10 rounded-tl-sm rounded-tr-sm sticky top-0 z-[3]"
                  >
                    Capacity
                  </TableHead>
                )}
              </TableRow>

              {/* Column header row */}
              <TableRow className="border-b border-black/10">
                <TableHead
                  onClick={() => toggleSort("label")}
                  className="px-2 h-[40px] py-0 text-left text-gray-500 font-medium cursor-pointer select-none hover:text-gray-800 transition-colors whitespace-nowrap bg-white sticky top-[21px] z-[3]"
                >
                  {entityLabel}
                  <SortArrow colKey="label" activeKey={activeSortKey} dir={sort.dir} />
                </TableHead>
                {[...utilCols, ...capCols].map((col) => (
                  <TableHead
                    key={col.id}
                    onClick={() => toggleSort(col.key)}
                    className="px-2 h-[40px] py-0 text-right text-gray-500 font-medium cursor-pointer select-none hover:text-gray-800 transition-colors whitespace-nowrap bg-white sticky top-[21px] z-[3]"
                  >
                    {/* The tooltip wraps the LABEL, not the cell: the whole cell
                      is the sort control, and a tooltip on it would follow the
                      pointer across a target the width of the column. */}
                    {col.tip ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="decoration-dotted underline-offset-2 hover:underline">
                            {col.label}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          showArrow={false}
                          className="max-w-[280px] border border-black/10 bg-white text-left font-normal text-gray-600 shadow-lg"
                        >
                          {METRIC_TOOLTIPS[col.tip]}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      col.label
                    )}
                    <SortArrow colKey={col.key} activeKey={activeSortKey} dir={sort.dir} />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {/* Pinned rows */}
              {pinned.map((row, i) => {
                const pinnedCell = {
                  background: "var(--brand-cream)",
                  top: `${61 + i * 39}px`,
                };
                return (
                  <TableRow key={`pinned-${i}`} className="border-b border-black/5">
                    <TableCell
                      className="px-2 py-2.5 text-left text-[12px] italic text-gray-500 font-medium truncate sticky z-[2]"
                      style={pinnedCell}
                    >
                      {row.label}
                    </TableCell>
                    {[...utilCols, ...capCols].map((col) => (
                      <TableCell
                        key={col.id}
                        className="px-2 py-2.5 text-right text-[12px] italic text-gray-500 sticky z-[2]"
                        style={pinnedCell}
                      >
                        {renderPinnedCell(row.metrics, col, row.siteCount)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}

              {/* Data rows */}
              {sorted.map((row) => {
                // The row matching the current selection at this level. The list
                // is no longer filtered down to it (see drillScope.ts), so the
                // highlight is the only thing saying which one you are looking at.
                const isSelected = row.id === selectedId;
                return (
                  <TableRow
                    key={row.id}
                    onClick={() => handleRowClick(row)}
                    aria-current={isSelected ? "true" : undefined}
                    className={[
                      "border-b border-black/5 last:border-0 cursor-pointer transition-colors",
                      isSelected
                        ? "bg-[color-mix(in_srgb,var(--brand-light-teal)_30%,white)] hover:bg-[color-mix(in_srgb,var(--brand-light-teal)_38%,white)]"
                        : "hover:bg-[color-mix(in_srgb,var(--brand-cream)_40%,white)]",
                    ].join(" ")}
                  >
                    <TableCell
                      className={[
                        "px-2 py-2.5 text-left truncate",
                        isSelected ? "font-semibold" : "font-medium",
                      ].join(" ")}
                      style={isSelected ? { color: "var(--brand-dark-teal)" } : undefined}
                    >
                      {row.label}
                    </TableCell>
                    {[...utilCols, ...capCols].map((col) => (
                      <TableCell key={col.id} className="px-2 py-2.5 text-right">
                        {renderCell(row.metrics, col, row.siteCount)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}

              {sorted.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={1 + utilCols.length + capCols.length}
                    className="py-8 text-center text-gray-400 text-[13px]"
                  >
                    No data for the selected filters
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </Card>
    </TooltipProvider>
  );
}
